import React, { useState, useContext } from "react";
import { AccountContext } from "./../../store/AccountContext";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import usePromise from "react-use-promise";
import MembershipProcess from "./MembershipProcess";
import ProfileLoading from "./ProfileLoading";
import { useToasts } from "react-toast-notifications";
import { CurrencySymbol } from "@booyaltd/core";

/**
 * PM5 T2 (money path, batch "myaccount-membership") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * SUBSCRIPTIONS ESCALATION CLASS. Styling-only: no handler, condition, state
 * value, prop or DOM node moved. Every rule below is the v4 rule it replaces;
 * each place a v5+ library default would have changed the rendering is
 * compensated for with a comment.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `background`, `myAccountContainer`,
 * `myAccountGridContainer` and `myAccountTitle` were declared but never
 * referenced from the JSX (copy-paste from MyAccount.js), so they rendered
 * nothing. `formContainer` was an object whose only two keys are commented
 * out — JSS emitted an empty rule, so the class applied no declarations; the
 * `<Grid container>` that carried it therefore takes no `sx`.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().activeBreadcrumb — a react-router <Link>, which takes a
// `className` but has no `sx` prop (guide §1.2).
const ActiveBreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ NEW DELTA (guide §2). v4's `.MuiGrid-container` declared `width: 100%`;
 * v9's Grid container has NO width rule at all — `generateGridStyles` emits
 * only `display: flex` / `flex-wrap` / `gap`. Harmless for a block-level
 * container, but a container nested DIRECTLY inside another container is a
 * flex item, and `width: auto` makes it shrink-to-fit. Measured in Chromium
 * on the exact emitted CSS: 800px in v4, 32px in v9, restored to 800px by
 * this rule. Restated verbatim on every converted container.
 */
const v4GridContainerWidthSx = { width: "100%" };

// was: useStyles().breadcrumbContainer
const breadcrumbContainerSx = (theme) => ({
  margin: theme.spacing(1, 0, 2, 0),
});

// was: useStyles().disabledBreadcrumb
const disabledBreadcrumbSx = { color: "#777777" };

// was: useStyles().myAccountSubTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const myAccountSubTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().myAccountThirdTitle
const myAccountThirdTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.2rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.2rem",
    lineHeight: "1.2rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  marginBottom: theme.spacing(2),
  "& span": {
    fontWeight: 500,
  },
});

// was: useStyles().bodyTextSml
const bodyTextSmlSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  "& span": {
    fontWeight: 500,
  },
});

// was: useStyles().submit, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted the `default`
// colour and made `primary` the default, which here would paint the label lime
// on the lime `typography.button` background — an invisible button.
const submitSx = (theme) => ({
  width: "100%",
  margin: theme.spacing(0, 0, 2, 0),
  [theme.breakpoints.up("sm")]: {
    width: "auto",
  },
  color: "text.primary",
});

export default function AutoRenewOn() {
  const { addToast } = useToasts();

  // Account Context
  const {
    handleResumeMembership,
    userProfile,
    getMembershipDetails,
    updating,
  } = useContext(AccountContext);

  // State
  // eslint-disable-next-line
  const [reloadTrigger, setReloadTrigger] = useState(Date.now());
  const [membershipDetails, , state] = usePromise(getMembershipDetails, [
    reloadTrigger,
  ]);

  const loading = state === "pending";

  // const reload = () => setReloadTrigger(Date.now());

  /*
    Are they an exisiting Stripe Customer?

    Yes
      No current subscription
        Select membership
        Show payment method on file or add a new payment method

      Current subscription in cancelled state
        Resume subscription

    No
      Add new payment method - if currently on a giftcard, this needs to pause the sub until the giftcard has expired
  */

  const handleTurnOnAutoRenew = async () => {
    try {
      // Await the result so the success toast only fires once the resume
      // actually resolves; a rejection lands in catch instead of being
      // swallowed as an unhandled promise (#567).
      await handleResumeMembership();
      addToast("Auto Renew Enabled.", {
        appearance: "success",
      });
    } catch (e) {
      addToast(e.toString(), {
        appearance: "error",
      });
    }
  };

  // #773 — `userProfile` is undefined until AccountContext resolves the profile
  // (AccountContext.tsx:69/:170). The `userProfile.memberStatus` reads below sit
  // inside a `{membershipDetails && …}` branch, so this route threw whenever the
  // membership fetch won the race against the profile — measured at roughly a
  // quarter of cold loads, which is why it looked intermittent. The throw was
  // turned into a blank page by the fallback-less error boundary (#774). Safe as
  // an early return because it sits AFTER every hook in this component
  // (`usePromise` included), so the hook order is identical on the loading and
  // loaded renders.
  if (!userProfile) {
    return <ProfileLoading />;
  }

  return (
    <Grid container sx={v4GridContainerWidthSx}>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbContainerSx}>
          <ActiveBreadcrumbLink to="/myaccount/overview">
            Overview
          </ActiveBreadcrumbLink>
          <Typography sx={disabledBreadcrumbSx}>Turn On Auto Renew</Typography>
        </Breadcrumbs>
      </Grid>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Typography variant="h2" sx={myAccountSubTitleSx}>
          Turn On Auto Renew
        </Typography>
      </Grid>
      <Grid container sx={v4GridContainerWidthSx}>
        {!loading && (
          <>
            {/* If they have Stripe Details */}
            {membershipDetails && (
              <>
                {!updating && (
                  <>
                    {/* If user is standard and productAutoRenewing is on, all is good */}
                    {userProfile.memberStatus === "standard" &&
                      userProfile.productAutoRenewing && (
                        <>
                          <Grid size={{ xs: 12, sm: 12 }}>
                            <Typography variant="body1" sx={bodyTextSx}>
                              Auto Renew has been successfully enabled on your
                              account.
                            </Typography>
                            <Link
                              to="/myaccount/overview"
                              style={{ textDecoration: "none" }}
                            >
                              <Button sx={submitSx}>
                                Go to Account Overview
                              </Button>
                            </Link>
                          </Grid>
                        </>
                      )}

                    {/* If user is standard and productAutoRenewing is off, get them onboard again */}
                    {userProfile.memberStatus === "standard" &&
                      !userProfile.productAutoRenewing &&
                      membershipDetails.creditCard && (
                        <>
                          <Grid size={{ xs: 12, sm: 12 }}>
                            <Typography variant="h3" sx={myAccountThirdTitleSx}>
                              Changed your mind?
                            </Typography>
                            <Typography variant="body1" sx={bodyTextSx}>
                              You recently disabled Auto Renew on your FarmFLiX
                              Membership.
                            </Typography>
                            <Typography variant="body1" sx={bodyTextSx}>
                              By enabling Auto Renew again, you&apos;ll be
                              charged{" "}
                              <span>
                                {CurrencySymbol({
                                  currency: membershipDetails.currency,
                                })}
                                {membershipDetails.cost}
                              </span>{" "}
                              automatically on&nbsp;
                              <span>
                                {userProfile.subscriptionExpiryDate &&
                                  new Intl.DateTimeFormat("en-GB", {
                                    year: "numeric",
                                    month: "long",
                                    day: "2-digit",
                                  }).format(
                                    new Date(
                                      userProfile.subscriptionExpiryDate,
                                    ),
                                  )}
                              </span>
                              .{" "}
                            </Typography>
                            <Button
                              onClick={handleTurnOnAutoRenew}
                              sx={submitSx}
                            >
                              Turn On Auto Renew
                            </Button>
                          </Grid>
                        </>
                      )}

                    {userProfile.memberStatus === "standard" &&
                      !userProfile.productAutoRenewing &&
                      !membershipDetails.creditCard && (
                        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                          <MembershipProcess />
                        </Grid>
                      )}

                    {/* If user is not standard, send them into the membership process */}
                    {userProfile.memberStatus !== "standard" && (
                      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <MembershipProcess />
                      </Grid>
                    )}
                  </>
                )}
              </>
            )}
            {/* No Stripe Details */}
            {!membershipDetails && (
              <>
                <MembershipProcess />
              </>
            )}
            {updating && (
              <Box sx={{ m: 5 }} align="center">
                <CircularProgress variant="indeterminate"></CircularProgress>
                <Typography variant="body2" sx={bodyTextSmlSx}>
                  <br />
                  Updating... Please do not refresh your browser.
                </Typography>
              </Box>
            )}
          </>
        )}
        {loading && (
          <Grid container sx={v4GridContainerWidthSx}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Box sx={{ m: 5 }} align="center">
                <CircularProgress variant="indeterminate"></CircularProgress>
              </Box>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
